/**
 * @fileoverview This file indexes and embeds documents using either local models or Voyage AI, leveraging `CachingEmbedder` for caching. It uses HNSW indexing (`HierarchicalNSW`) via `doEmbeddingIndex` to enable KNN searches for similar documents. Key functions include `embedVoyage` and `embedEmbeddingJs`.
 */

import { batch } from '@iterator/shared-common/src/batch'
import { debug, debug_block, error_block, info } from '@iterator/shared-node/util/logging'
import { time, Timing } from '@iterator/shared-node/util/timing'
import {
  Document,
  EmbeddedDocument,
  Embedding,
} from '@node_modules/@iterator/shared-node/ai/llm_types'
import { ensureAwolfDirs, getAwolfPaths } from '@src/awolf_dir'
import { walkFolder } from '@src/folder'
import { baseOfSparsePath, SparsePath } from '@iterator/shared-node/util/sparse_path'
import { createHash } from 'crypto'
import * as fs from 'fs'
import { HierarchicalNSW } from 'hnswlib-node'
import path = require('path')
import { embedVoyage, Voyage3Lite } from '@iterator/shared-node/ai/voyage'
import { AwolfSettings } from '@src/settings'

const embeddings = import('@themaximalist/embeddings.js')
type HashedDocument = Document & { md5: string }
type LocalEmbeddedDocument = EmbeddedDocument & { path: string }
interface BatchEmbedder {
  name: string
  embedBatch: (strs: string[]) => Promise<EmbeddedDocument[]>
}

export async function doEmbeddingIndex(
  directory: SparsePath,
  query?: string,
  embeddingName: EmbeddingName = DefaultEmbedderName,
  cacheKey?: string,
): Promise<{ paths?: string[]; timings: Timing[] }> {
  debug_block('doEmbeddingIndex', { directory, query, embeddingName })

  const paths: string[] = []

  const timings: Timing[] = []

  const { fileEmbedder, timing: loadIndexTiming } = await time('loadIndex', async () => ({
    fileEmbedder: await new CachingEmbedder(
      getEmbedder(embeddingName),
      baseOfSparsePath(directory),
      cacheKey,
    ).loadIndex(),
  }))
  timings.push(loadIndexTiming)

  walkFolder(directory, (path, type) => {
    if (type === 'file') {
      paths.push(path)
    }
  })
  debug(`Scan complete. Found ${paths.length} files.`)
  if (paths.length === 0) {
    info(`No files found in ${directory}.`)
    return { timings }
  }

  const { embeddings, timing: generateEmbeddingsTiming } = await time('generateEmbeddings', () =>
    generateEmbeddings(paths, fileEmbedder),
  )
  timings.push(generateEmbeddingsTiming)
  const { index, timing: buildIndexTiming } = await time('buildIndex', () =>
    Promise.resolve({ index: buildIndex(embeddings) }),
  )
  timings.push(buildIndexTiming)

  let neighborPaths: string[] | undefined = undefined
  if (query) {
    const { neighborPaths: knnNeighborPaths, timing: knnTiming } = await time('knn', () =>
      knn(index, fileEmbedder, paths, query, 10),
    )
    neighborPaths = knnNeighborPaths
    timings.push(knnTiming)
    debug_block('KNN Results', { neighborPaths })
  }

  debug_block('Timings', timings)
  debug(`Initial scan complete. Found ${paths.length} files. Exiting.`)
  return { paths: neighborPaths, timings }
}

async function generateEmbeddings(paths: string[], fileEmbedder: CachingEmbedder) {
  const batches = batch(paths, 10)
  const embeddings: LocalEmbeddedDocument[] = []
  let batchesComplete = 0
  for (const batch of batches) {
    const batchDocuments = batch
      .map((p) => ({
        path: p,
        content: fs.readFileSync(p, 'utf8'),
      }))
      .filter((f) => {
        const contentNonEmpty = f.content.length > 0
        if (!contentNonEmpty) {
          debug(`Skipping ${f.path} because it is empty`)
        }
        return contentNonEmpty
      })
    try {
      const batchEmbeddings = await fileEmbedder.embedBatch(batchDocuments.map((d) => d.content))
      embeddings.push(...batchEmbeddings.map((e, i) => ({ ...e, path: batch[i]! })))
      batchesComplete++
      debug(`Completed batch ${batchesComplete}/${batches.length}`)
    } catch (e) {
      error_block('Error embedding batch', {
        paths: batchDocuments.map((d) => d.path),
        error: e,
      })
    }
  }
  return { embeddings }
}

async function knn(
  index: HierarchicalNSW,
  embedder: BatchEmbedder,
  paths: string[],
  query: string,
  k: number,
): Promise<{ neighborPaths: string[] }> {
  const queryEmbedding = (await embedder.embedBatch([query]))[0]!.embedding
  const result = index.searchKnn(queryEmbedding, k)
  return { neighborPaths: result.neighbors.map((n) => paths[n]!) }
}

function buildIndex(embeddings: LocalEmbeddedDocument[]) {
  const numDimensions = embeddings[0]!.embedding.length
  const maxElements = embeddings.length
  const index = new HierarchicalNSW('l2', numDimensions)
  index.initIndex(maxElements, 16, 200, 100)
  for (let i = 0; i < embeddings.length; i++) {
    index.addPoint(embeddings[i]!.embedding, i)
  }
  return index
}

class CachingEmbedder implements BatchEmbedder {
  name: string
  private md5Cache: Map<string, Embedding> = new Map()
  private writeStream: fs.WriteStream | null = null

  constructor(
    private embedder: BatchEmbedder,
    private workingDir: string,
    private cacheKey: string = 'default',
  ) {
    ensureAwolfDirs(getAwolfPaths(workingDir))
    this.name = `${embedder.name}-cached`
  }

  async embedBatch(strs: string[]): Promise<EmbeddedDocument[]> {
    const hashedDocuments: HashedDocument[] = await Promise.all(
      strs.map(async (s) => ({
        str: s,
        md5: createHash('md5').update(s).digest('hex'),
      })),
    )

    const cachedEmbeddings: EmbeddedDocument[] = hashedDocuments
      .filter((d) => this.md5Cache.has(d.md5))
      .map((d) => ({
        ...d,
        embedding: this.md5Cache.get(d.md5)!,
      }))
    const uncachedDocuments = hashedDocuments.filter((d) => !this.md5Cache.has(d.md5))
    const computedEmbeddings: EmbeddedDocument[] = await this.embedder.embedBatch(
      uncachedDocuments.map((d) => d.str),
    )

    for (let i = 0; i < computedEmbeddings.length; i++) {
      this.md5Cache.set(uncachedDocuments[i]!.md5, computedEmbeddings[i]!.embedding)
      this.writeToCache(uncachedDocuments[i]!.md5, computedEmbeddings[i]!.embedding)
    }

    return [...cachedEmbeddings, ...computedEmbeddings]
  }

  async loadIndex(): Promise<CachingEmbedder> {
    try {
      debug(`🔍 Loading index from ${this.indexPath()}`)
      const readStream = fs.createReadStream(this.indexPath(), 'utf8')
      const rl = require('readline').createInterface({
        input: readStream,
        crlfDelay: Infinity,
      })
      let lines = 0
      for await (const line of rl) {
        if (line.length > 0) {
          const [md5, embeddingStr] = line.split(' ')
          if (md5 && embeddingStr) {
            const embedding = embeddingStr.split(',').map(Number)
            this.md5Cache.set(md5, embedding)
            lines++
            if (lines % 50 === 0) {
              debug(`Loaded ${lines} lines`)
            }
          }
        }
      }
      debug(`🔍 Finished loading index from ${this.indexPath()}. Loaded ${lines} lines.`)
    } catch (e) {
      // File doesn't exist yet, that's ok
      debug('Caught and ignoring error:')
      debug(e)
    }
    return this
  }

  private writeToCache(md5: string, embedding: Embedding): void {
    const writeStream = this.getWriteStream()
    writeStream.write(`${md5} ${embedding.join(',')}\n`)
  }

  private getWriteStream(): fs.WriteStream {
    if (!this.writeStream) {
      fs.mkdirSync(path.dirname(this.indexPath()), { recursive: true })
      this.writeStream = fs.createWriteStream(this.indexPath(), {
        flags: 'a',
        encoding: 'utf8',
      })
    }
    return this.writeStream
  }

  private indexPath(): string {
    const awolfPaths = getAwolfPaths(this.workingDir)
    return path.join(awolfPaths.indices, `${this.embedder.name}.${this.cacheKey}.md5-map.dat`)
  }
}

const Voyage3LiteEmbedder: BatchEmbedder = {
  name: 'voyage',
  embedBatch: (strs: string[]) =>
    embedVoyage(strs, {
      batchSize: 10,
      model: Voyage3Lite,
      settings: AwolfSettings.singleton(),
    }),
}

const EmbeddingJsEmbedder: BatchEmbedder = {
  name: 'local',
  embedBatch: embedEmbeddingJs,
}

export type EmbeddingName = 'voyage' | 'local'
export const DefaultEmbedderName: EmbeddingName = 'voyage'

const getEmbedder = (embeddingName: EmbeddingName): BatchEmbedder => {
  switch (embeddingName) {
    case 'voyage':
      return Voyage3LiteEmbedder
    case 'local':
      return EmbeddingJsEmbedder
    default:
      throw new Error(`Unknown embedding name: ${embeddingName}. Options: 'voyage', 'local'`)
  }
}

export async function embedEmbeddingJs(strs: string[]): Promise<EmbeddedDocument[]> {
  const embeddingsModule = await embeddings
  const embed = embeddingsModule.default

  const results: EmbeddedDocument[] = []
  let totalTime = 0
  for (const s of strs) {
    const res = await time(`embed`, async () => ({
      embedding: await embed(s),
    }))
    totalTime += res.timing.durationMs
    results.push({
      str: s,
      embedding: res.embedding,
    })
  }
  debug(
    `Embedded ${strs.length} strings in ${totalTime}ms (avg ${(totalTime / strs.length).toFixed(2)}ms per embedding).`,
  )
  return results
}
